import React, { useState, useMemo } from 'react';

import { useBookingJobPolling } from '../hooks/useBookingJobPolling';
import { useMessages } from '../hooks/useMessages';
import { Message } from '../types';
import { transformApiMessageToUI } from '../utils/messageUtils';
import { createOptimisticMessages } from '../utils/optimisticMessages';

import ErrorState from './LoadingStates/ErrorState';
import MessageLoading from './LoadingStates/MessageLoading';
import MessageInput from './MessageInput';
import MessageList from './MessageList';

interface MessageAreaProps {
  activeChatId: string;
}

const MessageArea: React.FC<MessageAreaProps> = ({ activeChatId }) => {
  const {
    messages: apiMessages,
    loading: messagesLoading,
    error: messagesError,
    sendMessage,
  } = useMessages(activeChatId);

  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);

  const apiMessagesTransformed: Message[] = useMemo(() => {
    return apiMessages.map(transformApiMessageToUI);
  }, [apiMessages]);

  useBookingJobPolling(apiMessagesTransformed, activeChatId);

  const messages: Message[] = useMemo(() => {
    return [...apiMessagesTransformed, ...optimisticMessages];
  }, [apiMessagesTransformed, optimisticMessages]);

  const handleSendMessage = async (
    messageText: string,
    attachmentKey?: string,
    attachmentFileName?: string,
    receiptId?: string
  ): Promise<void> => {
    if (!messageText.trim() && !attachmentKey) return;

    const { userMessage, thinkingMessage } = createOptimisticMessages(
      messageText,
      attachmentFileName
    );

    setOptimisticMessages([userMessage, thinkingMessage]);

    try {
      await sendMessage({
        text: messageText,
        attachmentStorageKey: attachmentKey,
        receiptId,
      });
      setOptimisticMessages([]);
    } catch (error) {
      console.error('Failed to send message:', error);
      setOptimisticMessages([]);
    }
  };

  return (
    <>
      <div className='flex-1 overflow-y-auto min-h-0'>
        {messagesLoading ? (
          <MessageLoading />
        ) : messagesError ? (
          <ErrorState
            title='Error loading messages'
            message={messagesError}
          />
        ) : (
          <MessageList messages={messages} />
        )}
      </div>

      <div className='flex-shrink-0'>
        <MessageInput
          key={activeChatId}
          onSendMessage={handleSendMessage}
          disabled={messagesLoading}
        />
      </div>
    </>
  );
};

export default MessageArea;
